import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  MyVolunteerSignup,
  fetchAllMyVolunteerSignups,
  cancelVolunteerSignupByToken,
} from "../../api/eventVolunteersAPI";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { Button, Card, SectionHeading } from "../ui";

/**
 * Member dashboard card showing all upcoming volunteer commitments
 * across every event the user has signed up for. Quick cancel from
 * the row, deep-link to the event page for swap/full management.
 */
interface Props {
  authToken: string | null;
}

const STATUS_BADGE: Record<string, string> = {
  signed_up: "bg-blue-100 text-blue-800",
  confirmed: "bg-green-100 text-green-800",
  waitlisted: "bg-amber-100 text-amber-800",
  cancelled: "bg-gray-200 text-gray-600",
  completed: "bg-emerald-100 text-emerald-800",
  no_show: "bg-red-100 text-red-800",
};

const MyVolunteerSignupsCard: React.FC<Props> = ({ authToken }) => {
  const [signups, setSignups] = useState<MyVolunteerSignup[]>([]);
  const [loaded, setLoaded] = useState(false);
  const { confirm, confirmModal } = useConfirmModal();

  async function load() {
    const r = await fetchAllMyVolunteerSignups(authToken);
    setSignups(r);
    setLoaded(true);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Filter to upcoming + active commitments. Past + cancelled hide
  // unless explicitly toggled (deferred — for now just hide them so
  // the dashboard card stays compact).
  const visible = signups.filter((s) => {
    if (s.status === "cancelled" || s.status === "no_show") return false;
    if (s.role_end_at) return new Date(s.role_end_at) >= new Date();
    return true; // unknown end date — keep visible
  });

  async function handleCancel(s: MyVolunteerSignup) {
    if (!(await confirm({
      title: "Cancel signup?",
      message: `Cancel your signup for "${s.role_name}" at "${s.event_name}"?`,
      confirmText: "Cancel signup",
      cancelText: "Keep it",
    }))) return;
    const r = await cancelVolunteerSignupByToken(s.event_id, s.cancellation_token);
    if (r?.success) {
      toast.success("Canceled");
      await load();
    } else toast.error((r as any)?.error || "Cancel failed");
  }

  if (!loaded) return null;
  if (visible.length === 0) return null;

  const tenant = window.location.pathname.split("/").filter(Boolean)[0] || "";

  return (
    <Card padding="md" className="mb-4 sm:mb-6">
      {confirmModal}
      <SectionHeading>My volunteer signups</SectionHeading>
      <ul className="divide-y text-sm">
        {visible.map((s) => (
          <li key={s.id} className="py-2 flex items-baseline justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Link
                to={`/${tenant}/events/${s.event_id}`}
                className="font-semibold text-blue-700 hover:underline"
              >
                {s.event_name || "Event"}
              </Link>
              <div className="text-xs text-gray-600">
                {s.role_name}
                {s.role_start_at ? ` · ${new Date(s.role_start_at).toLocaleString()}` : ""}
              </div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGE[s.status] || "bg-gray-100"}`}>
              {s.status.replace(/_/g, " ")}
            </span>
            <Button variant="dangerGhost" size="sm" onClick={() => handleCancel(s)}>
              Cancel
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
};

export default MyVolunteerSignupsCard;
